import "../global.css";
import { useEffect, useState, useRef } from "react";
import { Stack, useRouter, useSegments } from "expo-router";
import { StatusBar } from "expo-status-bar";
import * as SplashScreen from "expo-splash-screen";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { AppState, AppStateStatus, Platform } from "react-native";
import * as ExpoNotifications from "expo-notifications";
import { AuthProvider, useAuth } from "@/providers/AuthProvider";
import { ThemeProvider, useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { View } from "react-native";
import { NotificationOverlay } from "@/components/NotificationOverlay";
import { AnimatedSplashScreen } from "@/components/AnimatedSplashScreen";
import { Loader } from "@/components/Loader";

/** Check if running on web platform */
const isWeb = Platform.OS === "web";

const INTRO_SEEN_KEY = "@staatsen_intro_seen";

// Prevent the native splash screen from auto-hiding
SplashScreen.preventAutoHideAsync();

const queryClient = new QueryClient();

/**
 * Simplified layout for web-only static pages (privacy policy, terms, etc.)
 * No auth, no splash screen, just the content
 */
function WebOnlyLayout() {
  return (
    <View style={{ minHeight: "100vh", overflow: "visible" } as any}>
      <Stack screenOptions={{ headerShown: false }}>
        <Stack.Screen name="(web)" />
      </Stack>
    </View>
  );
}

/**
 * Navigation component that handles auth-based routing
 */
function RootLayoutNav() {
  const { user, isInitialized, isLoading } = useAuth();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const segments = useSegments();
  const router = useRouter();
  const [showSplash, setShowSplash] = useState(!isWeb); // No splash on web
  const [appIsReady, setAppIsReady] = useState(isWeb); // Ready immediately on web
  const [hasSeenIntro, setHasSeenIntro] = useState<boolean | null>(isWeb ? true : null);
  const appState = useRef(AppState.currentState);
  const hasResetToHome = useRef(false);

  // Check if currently in web route group
  const inWebGroup = segments[0] === "(web)";

  // On web platform in (web) routes, render simplified layout
  if (isWeb && inWebGroup) {
    return <WebOnlyLayout />;
  }

  // Check if user has seen intro screen (skip on web)
  useEffect(() => {
    if (isWeb) return;
    const checkIntroSeen = async () => {
      const seen = await AsyncStorage.getItem(INTRO_SEEN_KEY);
      setHasSeenIntro(seen === "true");
    };
    checkIntroSeen();
  }, []);

  // Reset to home when app is opened (cold start or from background) - skip on web
  useEffect(() => {
    if (isWeb) return;
    if (!isInitialized || !user || hasResetToHome.current) return;

    // Reset to home tab on initial load
    const currentSegment = segments[0];
    if (currentSegment !== "(auth)" && currentSegment !== "(tabs)") {
      router.replace("/(tabs)");
    }
    hasResetToHome.current = true;
  }, [isInitialized, user]);

  // Handle app state changes (background -> foreground) - skip on web
  // Clears the app badge count when the user returns to the app
  useEffect(() => {
    if (isWeb) return;
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (appState.current.match(/inactive|background/) && nextAppState === "active") {
        ExpoNotifications.setBadgeCountAsync(0);
      }
      appState.current = nextAppState;
    };

    const subscription = AppState.addEventListener("change", handleAppStateChange);
    return () => subscription.remove();
  }, []);

  // Auth-based routing - redirect to (web) on web platform
  useEffect(() => {
    // On web, redirect to web pages if not already there
    if (isWeb && !inWebGroup) {
      router.replace("/(web)");
      return;
    }

    if (!isInitialized || hasSeenIntro === null) return;

    const inAuthGroup = segments[0] === "(auth)";

    if (!user && !inAuthGroup) {
      // Show intro screen on first launch, otherwise go to login
      if (!hasSeenIntro) {
        router.replace("/(auth)/intro");
      } else {
        router.replace("/(auth)/login");
      }
    } else if (user && inAuthGroup) {
      // Redirect to home if authenticated and in auth screens
      router.replace("/(tabs)");
    }
  }, [user, segments, isInitialized, hasSeenIntro, inWebGroup]);

  // Mark app as ready when initialized
  useEffect(() => {
    if (isWeb) return;
    if (isInitialized && !isLoading) {
      setAppIsReady(true);
    }
  }, [isInitialized, isLoading]);

  const handleSplashFinish = async () => {
    setShowSplash(false);
    // Hide the native splash screen
    await SplashScreen.hideAsync();
  };

  return (
    <>
      <View className={`flex-1 ${isDark ? "bg-slate-900" : "bg-white"}`}>
        {!isInitialized || isLoading ? (
          <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
            <Loader size={120} />
          </View>
        ) : (
          <Stack screenOptions={{ headerShown: false }}>
            <Stack.Screen name="(auth)" />
            <Stack.Screen name="(tabs)" />
            <Stack.Screen name="(admin)" />
            <Stack.Screen name="(super-admin)" />
            <Stack.Screen name="(web)" />
            <Stack.Screen name="auth" />
            <Stack.Screen name="chat-screens" options={{ presentation: "modal" }} />
            <Stack.Screen name="chat-detail" options={{ headerShown: false }} />
            <Stack.Screen name="agenda" options={{ presentation: "modal" }} />
          </Stack>
        )}
        <StatusBar style={isDark ? "light" : "dark"} />
        {/* Global notification overlay with lottie animations */}
        {isInitialized && !isWeb && <NotificationOverlay />}
      </View>
      
      {/* Show animated splash screen until dismissed - not on web */}
      {showSplash && !isWeb && (
        <AnimatedSplashScreen
          onFinish={handleSplashFinish}
          isReady={appIsReady}
        />
      )}
    </>
  );
}

/**
 * Root layout component with providers
 */
export default function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <RootLayoutNav />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

